import { useState, useCallback } from 'react';
import { formatTRY } from '@/lib/currencyFormat';
import {
  profiloPreviewImport,
  profiloCommitImport,
  statusLabels,
  statusColors,
} from '@/lib/profiloImport';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type Step = 'upload' | 'preview' | 'committing' | 'done';

const PRICE_TYPE_OPTS = [
  { value: 'normal', label: 'Normal fiyat listesi' },
  { value: 'prim_harici', label: 'Prim harici fiyat listesi' },
  { value: 'her_ikisi', label: 'Her ikisi de (ayrı kaydet)' },
];

const MONTH_NAMES = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

export default function ProfiloPriceImportModal({ isOpen, onClose, onSuccess }: Props) {
  const [step, setStep] = useState<Step>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [priceType, setPriceType] = useState('normal');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewData, setPreviewData] = useState<any>(null);
  const [previewItems, setPreviewItems] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sheetFilter, setSheetFilter] = useState('all');
  const [commitResult, setCommitResult] = useState<any>(null);
  const [duplicateWarning, setDuplicateWarning] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  const handleClose = useCallback(() => {
    setStep('upload');
    setFile(null);
    setPriceType('normal');
    setLoading(false);
    setError(null);
    setPreviewData(null);
    setPreviewItems([]);
    setSearchQuery('');
    setStatusFilter('all');
    setSheetFilter('all');
    setCommitResult(null);
    setDuplicateWarning(false);
    setCurrentPage(1);
    onClose();
  }, [onClose]);

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.xlsx')) {
      setFile(f);
      setError(null);
    } else {
      setError('Yalnızca .xlsx dosyaları desteklenir.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f && f.name.endsWith('.xlsx')) {
      setFile(f);
      setError(null);
    } else {
      setError('Yalnızca .xlsx dosyaları desteklenir.');
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    try {
      const result = await profiloPreviewImport(file, priceType);
      if (result.error) {
        setError(result.error);
        return;
      }
      setPreviewData(result);
      setPreviewItems(result.preview || []);
      if (result.existingBatch) setDuplicateWarning(true);
      setStep('preview');
      setCurrentPage(1);
    } catch (e: any) {
      setError(e.message || 'Önizleme oluşturulamadı');
    } finally {
      setLoading(false);
    }
  };

  const handleCommit = async () => {
    setStep('committing');
    setLoading(true);
    setError(null);
    try {
      const toImport = previewItems.filter(
        (p) => p.status !== 'skipped' && p.status !== 'error'
      );
      const result = await profiloCommitImport(
        toImport,
        previewData.fileName || file?.name || 'Profilo Fiyat Listesi.xlsx',
        previewData.period,
        priceType
      );
      if (result.error) {
        setError(result.error);
        setStep('preview');
        return;
      }
      setCommitResult(result);
      setStep('done');
      onSuccess();
    } catch (e: any) {
      setError(e.message || 'İçe aktarma başarısız');
      setStep('preview');
    } finally {
      setLoading(false);
    }
  };

  const toggleItemStatus = (idx: number) => {
    setPreviewItems((prev) =>
      prev.map((item, i) => {
        if (i !== idx) return item;
        if (item.status === 'skipped') {
          return { ...item, status: item._originalStatus };
        }
        return { ...item, _originalStatus: item.status, status: 'skipped' };
      })
    );
  };

  const filteredItems = previewItems.filter((p) => {
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (sheetFilter !== 'all' && p.sheetName !== sheetFilter) return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      if (
        !p.rawModelCode?.toLowerCase().includes(q) &&
        !p.description?.toLowerCase().includes(q) &&
        !p.category?.toLowerCase().includes(q)
      ) return false;
    }
    return true;
  });

  const totalPages = Math.ceil(filteredItems.length / pageSize);
  const paginatedItems = filteredItems.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const summary = {
    total: previewItems.length,
    new: previewItems.filter((p) => p.status === 'new').length,
    update: previewItems.filter((p) => p.status === 'update').length,
    unchanged: previewItems.filter((p) => p.status === 'unchanged').length,
    error: previewItems.filter((p) => p.status === 'error').length,
    skipped: previewItems.filter((p) => p.status === 'skipped').length,
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-[200] flex items-start justify-center p-4 overflow-y-auto"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div className="bg-background-50 rounded-2xl shadow-xl w-full max-w-5xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center">
              <i className="ri-file-excel-line text-primary-600 text-lg"></i>
            </div>
            <div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading">Profilo Fiyat Listesi İçe Aktar</h2>
              <p className="text-xs text-foreground-400">
                {step === 'upload' && 'Excel dosyasını yükle'}
                {step === 'preview' && `${previewItems.length} ürün tespit edildi — onaylamayı bekleniyor`}
                {step === 'committing' && 'Kaydediliyor...'}
                {step === 'done' && 'Tamamlandı!'}
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <div className="p-6">
          {/* Error Alert */}
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5"></i>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          {/* STEP: UPLOAD */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Drop Zone */}
              <div
                className="border-2 border-dashed border-background-300 rounded-2xl p-12 text-center hover:border-primary-300 transition-colors cursor-pointer"
                onDrop={handleDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById('profilo-file-input')?.click()}
              >
                <div className="w-16 h-16 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-upload-cloud-line text-3xl text-primary-400"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-700 mb-1">
                  {file ? file.name : 'Excel dosyasını buraya sürükle veya tıkla'}
                </h3>
                <p className="text-sm text-foreground-400">
                  {file ? `${(file.size / 1024 / 1024).toFixed(1)} MB` : 'Yalnızca .xlsx dosyaları desteklenir'}
                </p>
                {file && (
                  <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-100 rounded-full">
                    <i className="ri-check-line text-emerald-600 text-sm"></i>
                    <span className="text-xs font-medium text-emerald-700">Dosya seçildi</span>
                  </div>
                )}
                <input
                  id="profilo-file-input"
                  type="file"
                  accept=".xlsx"
                  className="hidden"
                  onChange={handleFileChange}
                />
              </div>

              {/* Price Type */}
              <div>
                <label className="block text-sm font-medium text-foreground-700 mb-2">Fiyat Listesi Türü</label>
                <div className="space-y-2">
                  {PRICE_TYPE_OPTS.map((opt) => (
                    <label key={opt.value} className="flex items-center gap-3 p-3 border border-background-200 rounded-xl cursor-pointer hover:bg-background-100 transition-colors">
                      <input
                        type="radio"
                        name="priceType"
                        value={opt.value}
                        checked={priceType === opt.value}
                        onChange={(e) => setPriceType(e.target.value)}
                        className="accent-primary-500"
                      />
                      <span className="text-sm text-foreground-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 justify-end pt-2">
                <button onClick={handleClose} className="btn-outline cursor-pointer whitespace-nowrap">İptal</button>
                <button
                  onClick={handlePreview}
                  disabled={!file || loading}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  {loading ? (
                    <><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div><span>Analiz Ediliyor...</span></>
                  ) : (
                    <><i className="ri-eye-line"></i><span>Önizle ve İncele</span></>
                  )}
                </button>
              </div>
            </div>
          )}

          {/* STEP: PREVIEW */}
          {step === 'preview' && previewData && (
            <div className="space-y-4">
              {/* Debug Report - shown when 0 products detected */}
              {previewItems.length === 0 && previewData.debugReports && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-start gap-3 mb-3">
                    <i className="ri-error-warning-line text-red-500 flex-shrink-0 mt-0.5 text-lg"></i>
                    <div>
                      <p className="text-sm font-semibold text-red-800">Dosya okundu ancak ürün satırları eşleştirilemedi!</p>
                      <p className="text-xs text-red-600 mt-0.5">Parser debug raporu aşağıda. Lütfen geliştiriciyle paylaş.</p>
                    </div>
                  </div>
                  {previewData.debugReports.map((dbg: any, di: number) => (
                    <div key={di} className="mt-2 p-3 bg-white border border-red-100 rounded-lg text-xs">
                      <p className="font-semibold text-foreground-800 mb-2">
                        <i className="ri-file-list-line mr-1"></i>
                        {dbg.sheetName} — {dbg.detectedMethod} detection
                      </p>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-foreground-500">
                        <span>Toplam satır: <strong>{dbg.totalRows}</strong></span>
                        <span>Başlık satırı: <strong>{dbg.headerRowIndex !== null ? dbg.headerRowIndex : 'bulunamadı'}</strong></span>
                        <span>Model sütunu: <strong>{dbg.modelCol}</strong></span>
                        <span>Açıklama sütunu: <strong>{dbg.descriptionCol !== null ? dbg.descriptionCol : '—'}</strong></span>
                        <span>Maliyet sütunu: <strong>{dbg.costCol !== null ? dbg.costCol : '—'}</strong></span>
                        <span>Opsiyonlu sütunu: <strong>{dbg.optionalCostCol !== null ? dbg.optionalCostCol : '—'}</strong></span>
                        <span>Model kodu bulunan: <strong>{dbg.rowsWithModelCode}</strong></span>
                        <span>Sayısal fiyat bulunan: <strong>{dbg.rowsWithNumericPrice}</strong></span>
                        <span>Ürün kabul edilen: <strong className="text-red-600">{dbg.productRowsAccepted}</strong></span>
                      </div>
                      {dbg.skippedReasons?.length > 0 && (
                        <details className="mt-2">
                          <summary className="cursor-pointer text-foreground-500 hover:text-foreground-700">
                            Atlanan satır örnekleri ({dbg.skippedReasons.length} adet)
                          </summary>
                          <div className="mt-1 space-y-0.5 max-h-[150px] overflow-y-auto">
                            {dbg.skippedReasons.map((reason: string, ri: number) => (
                              <p key={ri} className="text-foreground-400 font-mono">{reason}</p>
                            ))}
                          </div>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Duplicate Warning */}
              {duplicateWarning && previewData.existingBatch && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-start gap-3">
                  <i className="ri-alert-line text-amber-500 flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-sm font-semibold text-amber-800">
                      {previewData.period ? `${MONTH_NAMES[(previewData.period.month || 1) - 1]} ${previewData.period.year}` : ''} Profilo fiyat listesi daha önce yüklenmiş!
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Önceki yükleme: {previewData.existingBatch.file_name} — {new Date(previewData.existingBatch.imported_at).toLocaleDateString('tr-TR')}
                    </p>
                    <p className="text-xs text-amber-600 mt-1">Devam etmek fiyatları günceller, geçmiş korunur.</p>
                  </div>
                </div>
              )}

              {/* Period and Sheets Info */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Tespit Edilen Ürün', value: summary.total, color: 'text-foreground-900' },
                  { label: 'Yeni Ürün', value: summary.new, color: 'text-emerald-600' },
                  { label: 'Güncellenecek', value: summary.update, color: 'text-amber-600' },
                  { label: 'Değişmedi', value: summary.unchanged, color: 'text-foreground-400' },
                ].map((s) => (
                  <div key={s.label} className="bg-background-100 rounded-xl p-3 text-center">
                    <p className={`text-xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Sheet Stats */}
              {previewData.sheetStats && (
                <div className="flex flex-wrap gap-2">
                  {Object.entries(previewData.sheetStats).map(([sheet, count]) => (
                    <span key={sheet} className="px-2.5 py-1 bg-background-100 rounded-full text-xs text-foreground-500 font-medium">
                      {sheet}: {count as number} ürün
                    </span>
                  ))}
                </div>
              )}

              {/* Unknown Sheets Warning */}
              {previewData.unknownSheets?.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700">
                  <i className="ri-information-line mr-1"></i>
                  Tanımlanamayan sayfa: <strong>{previewData.unknownSheets.join(', ')}</strong> — bu sayfalar atlandı.
                </div>
              )}

              {/* Filters */}
              <div className="flex flex-wrap gap-2">
                <div className="relative flex-1 min-w-[200px]">
                  <input
                    type="text"
                    placeholder="Model kodu ara..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
                    className="w-full pl-8 pr-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300"
                  />
                  <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-foreground-300 text-sm"></i>
                </div>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  className="px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer"
                >
                  <option value="all">Tüm Durumlar</option>
                  <option value="new">Yeni Ürün</option>
                  <option value="update">Güncellenecek</option>
                  <option value="unchanged">Değişmedi</option>
                  <option value="error">Hata</option>
                  <option value="skipped">Atlandı</option>
                </select>
                {previewData.detectedSheets?.length > 1 && (
                  <select
                    value={sheetFilter}
                    onChange={(e) => { setSheetFilter(e.target.value); setCurrentPage(1); }}
                    className="px-3 py-2 text-sm bg-background-50 border border-background-200 rounded-lg text-foreground-700 cursor-pointer"
                  >
                    <option value="all">Tüm Sayfalar</option>
                    {previewData.detectedSheets.map((s: string) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                )}
              </div>

              {/* Preview Table */}
              <div className="bg-background-50 rounded-xl border border-background-200 overflow-hidden">
                <div className="overflow-x-auto max-h-[400px]">
                  <table className="w-full min-w-[800px] text-xs">
                    <thead className="sticky top-0 bg-background-100 z-10">
                      <tr className="border-b border-background-200">
                        <th className="px-3 py-2 text-left text-foreground-500 font-medium">Durum</th>
                        <th className="px-3 py-2 text-left text-foreground-500 font-medium">Model Kodu</th>
                        <th className="px-3 py-2 text-left text-foreground-500 font-medium">Kategori</th>
                        <th className="px-3 py-2 text-left text-foreground-500 font-medium">Sayfa</th>
                        <th className="px-3 py-2 text-right text-foreground-500 font-medium">Eski Maliyet</th>
                        <th className="px-3 py-2 text-right text-foreground-500 font-medium">Yeni Maliyet</th>
                        <th className="px-3 py-2 text-right text-foreground-500 font-medium">Opsiyonlu</th>
                        <th className="px-3 py-2 text-center text-foreground-500 font-medium">İşlem</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedItems.map((item, idx) => {
                        const realIdx = previewItems.findIndex((p) => p === item);
                        return (
                          <tr key={idx} className={`border-b border-background-100 hover:bg-background-50 ${item.status === 'skipped' ? 'opacity-40' : ''}`}>
                            <td className="px-3 py-2">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusColors[item.status] || 'bg-background-100 text-foreground-500'}`}>
                                {statusLabels[item.status] || item.status}
                              </span>
                            </td>
                            <td className="px-3 py-2 font-mono font-medium text-foreground-800">{item.rawModelCode}</td>
                            <td className="px-3 py-2 text-foreground-500">{item.category}</td>
                            <td className="px-3 py-2 text-foreground-400">{item.sheetName}</td>
                            <td className="px-3 py-2 text-right text-foreground-400">
                              {item.oldCostPrice != null ? formatTRY(item.oldCostPrice) : '—'}
                            </td>
                            <td className="px-3 py-2 text-right">
                              <span className={item.status === 'update' ? 'font-semibold text-amber-600' : 'text-foreground-700'}>
                                {item.costPrice != null ? formatTRY(item.costPrice) : '—'}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-right text-foreground-400">
                              {item.optionalCostPrice != null ? formatTRY(item.optionalCostPrice) : '—'}
                            </td>
                            <td className="px-3 py-2 text-center">
                              <button
                                onClick={() => toggleItemStatus(realIdx)}
                                className={`w-6 h-6 rounded flex items-center justify-center cursor-pointer transition-colors ${item.status === 'skipped' ? 'bg-background-200 hover:bg-primary-100' : 'bg-background-100 hover:bg-red-50 hover:text-red-500'}`}
                                title={item.status === 'skipped' ? 'Dahil et' : 'Hariç tut'}
                              >
                                <i className={`text-xs ${item.status === 'skipped' ? 'ri-add-line' : 'ri-close-line'}`}></i>
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                      {paginatedItems.length === 0 && (
                        <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-foreground-400">Sonuç bulunamadı</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-4 py-3 border-t border-background-200 bg-background-100/50">
                    <span className="text-xs text-foreground-400">{filteredItems.length} sonuç, sayfa {currentPage}/{totalPages}</span>
                    <div className="flex items-center gap-1">
                      <button onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} disabled={currentPage === 1} className="w-7 h-7 rounded flex items-center justify-center cursor-pointer hover:bg-background-200 disabled:opacity-30">
                        <i className="ri-arrow-left-s-line text-sm"></i>
                      </button>
                      <button onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))} disabled={currentPage === totalPages} className="w-7 h-7 rounded flex items-center justify-center cursor-pointer hover:bg-background-200 disabled:opacity-30">
                        <i className="ri-arrow-right-s-line text-sm"></i>
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Notice */}
              <div className="p-3 bg-background-100 rounded-xl text-xs text-foreground-500 flex items-start gap-2">
                <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
                <span>Satış fiyatları değiştirilmeyecek. Maliyet güncellemesi fiyat geçmişine kaydedilecek. Stok miktarları etkilenmeyecek.</span>
              </div>

              {/* Actions */}
              <div className="flex gap-3 justify-end pt-2">
                <button onClick={() => setStep('upload')} className="btn-outline cursor-pointer whitespace-nowrap">
                  <i className="ri-arrow-left-line mr-1"></i>Geri
                </button>
                <button
                  onClick={handleCommit}
                  disabled={summary.new + summary.update === 0}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-check-line"></i>
                  <span>
                    {summary.new + summary.update > 0
                      ? `${summary.new + summary.update} Ürünü Kaydet`
                      : 'Kaydedilecek ürün yok'}
                  </span>
                </button>
              </div>
            </div>
          )}

          {/* STEP: COMMITTING */}
          {step === 'committing' && (
            <div className="flex flex-col items-center gap-4 py-12">
              <div className="w-16 h-16 rounded-full border-4 border-primary-100 border-t-primary-500 animate-spin"></div>
              <h3 className="text-lg font-semibold text-foreground-800">İçe Aktarılıyor...</h3>
              <p className="text-sm text-foreground-400">Ürünler ve fiyatlar kaydediliyor, lütfen bekleyin.</p>
            </div>
          )}

          {/* STEP: DONE */}
          {step === 'done' && commitResult && (
            <div className="space-y-6">
              <div className="flex flex-col items-center gap-3 py-6">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                  <i className="ri-check-line text-3xl text-emerald-500"></i>
                </div>
                <h3 className="text-xl font-bold text-foreground-900">İçe Aktarma Tamamlandı!</h3>
                {previewData?.period && (
                  <p className="text-sm text-foreground-400">
                    {MONTH_NAMES[(previewData.period.month || 1) - 1]} {previewData.period.year} Profilo fiyat listesi başarıyla aktarıldı.
                  </p>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[
                  { label: 'Toplam İşlenen', value: commitResult.summary?.total || 0, color: 'text-foreground-900' },
                  { label: 'Yeni Ürün', value: commitResult.summary?.new || 0, color: 'text-emerald-600' },
                  { label: 'Güncellenen', value: commitResult.summary?.updated || 0, color: 'text-amber-600' },
                  { label: 'Hata', value: commitResult.summary?.errors || 0, color: 'text-red-500' },
                ].map((s) => (
                  <div key={s.label} className="bg-background-100 rounded-xl p-4 text-center">
                    <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
                    <p className="text-xs text-foreground-400 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              <div className="p-4 bg-background-100 rounded-xl text-sm text-foreground-600">
                <i className="ri-information-line mr-2"></i>
                Satış fiyatları değiştirilmedi. Maliyet geçmişi kaydedildi.
              </div>

              <div className="flex justify-end">
                <button onClick={handleClose} className="btn-primary cursor-pointer whitespace-nowrap">
                  Kapat
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}